import { useCost } from '@/hooks/useCost';
import { type ActiveGeneration, useGenerationQueue } from '@/hooks/useGenerationQueue';
import { usePrompt } from '@/hooks/usePrompt';
import { useSettings } from '@/hooks/useSettings';
import { useSubmission } from '@/hooks/useSubmission';
import { useSubscription } from '@/hooks/useSubscription';
import { useEffect, useMemo, useRef } from 'react';

import type { GeneratorMetadata } from '@/lib/types/generator';

export type { ActiveGeneration };

export function useGenerator(generator?: GeneratorMetadata) {
  const attemptAutoSwitchRef = useRef<any>(null);

  // Custom Hooks — each hook fetches its own data independently
  const {
    isAuthenticated,
    user,
    isSignupModalOpen,
    setIsSignupModalOpen,
    isPricingModalOpen,
    setIsPricingModalOpen,
    isTrialModalOpen,
    setIsTrialModalOpen,
    subscriptionInfo,
    credits,
    totalCredits,
    setCredits,
    refreshCreditsAndSubscription,
    isAuthInitialized,
  } = useSubscription({
    initialCredits: null,
    initialSubscription: null,
  });

  const {
    userContent,
    setUserContent,
    isGenerating,
    setIsGenerating,
    activeGenerations,
    setActiveGenerations,
    loadingTextIndex,
    setLoadingTextIndex,
    showEncouragement,
    loadingTextAutoplay,
    setLoadingTextAutoplay,
    isInitialLoading,
  } = useGenerationQueue({
    isAuthenticated,
    isAuthInitialized,
    onCreditUpdate: refreshCreditsAndSubscription,
    initialContent: null,
    userId: user?.id,
    onAutoSwitch: (slug, params) => attemptAutoSwitchRef.current?.(slug, params),
  });

  // 1. Prompt Hook
  const { contentInput, setContentInput, contentEditableRef, handleInput } = usePrompt({
    isAuthenticated,
    isAuthInitialized,
    isPricingModalOpen,
  });

  // 2. Settings Hook
  const settings = useSettings({
    isAuthenticated,
    isAuthInitialized,
    setContentInput,
    defaultModel: generator?.modelId,
  });

  // 3. Cost Hook
  const { services, estimatedCost } = useCost({
    model: settings.model,
    resolution: settings.resolution,
    duration: settings.duration,
    variations: settings.variations,
    imageUrl: settings.selectedImage,
    imageUrls: settings.selectedImages,
  });

  // Derive free preview eligibility from client-side data
  // Mirrors server-side checkFreePreviewEligibility in free-preview.ts:
  //   1. balance === 0
  //   2. No prior non-failed generated content
  //   3. No active subscription
  const isFreePreviewEligible = useMemo(() => {
    // credits is falsy for both null (not loaded) and 0 (genuinely zero)
    if (credits) return false;
    const hasNonFailedContent = userContent.some((c: any) => c.status !== 'failed');
    if (hasNonFailedContent) return false;
    if (subscriptionInfo) return false;
    return true;
  }, [credits, userContent, subscriptionInfo]);

  // 4. Submission Hook
  const { isSubmitting, handleSubmit, attemptAutoSwitch } = useSubmission({
    isAuthenticated,
    contentInput,
    setContentInput,
    activeGenerations,
    credits,
    setCredits,
    estimatedCost,
    subscriptionInfo,
    model: settings.model,
    aspectRatio: settings.aspectRatio,
    resolution: settings.resolution,
    duration: settings.duration,
    variations: settings.variations,
    mode: settings.mode,
    selectedImage: settings.selectedImage,
    selectedImages: settings.selectedImages,
    setSelectedImage: settings.setSelectedImage,
    setSelectedImages: settings.setSelectedImages,
    setIsSignupModalOpen,
    setIsPricingModalOpen,
    setIsTrialModalOpen,
    setIsGenerating,
    setLoadingTextIndex,
    setLoadingTextAutoplay,
    refreshCreditsAndSubscription,
    setActiveGenerations,
    contentEditableRef,
    services,
    autoSwitchEnabled: settings.autoSwitchEnabled,
    isFreePreviewEligible,
  });

  // Sync ref for polling failures
  useEffect(() => {
    attemptAutoSwitchRef.current = attemptAutoSwitch;
  }, [attemptAutoSwitch]);

  // Refs and Handlers
  const controlsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { setActiveDropdown } = settings;
  // Click Outside Handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!controlsRef.current) return;

      const target = event.target as Element;

      const isOutsideControls = !controlsRef.current.contains(target as Node);
      const isControlTrigger = target.closest('.custom-select') !== null;
      const isDropdownContent = target.closest('.select-options') !== null;

      if (isOutsideControls || (!isControlTrigger && !isDropdownContent)) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setActiveDropdown]);

  return {
    // Auth & Subscription
    isAuthenticated,
    user,
    isSignupModalOpen,
    setIsSignupModalOpen,
    isPricingModalOpen,
    setIsPricingModalOpen,
    isTrialModalOpen,
    setIsTrialModalOpen,
    subscriptionInfo,
    credits,
    totalCredits,
    isAuthInitialized,
    refreshCreditsAndSubscription,

    // Generation Queue
    userContent,
    setUserContent,
    isGenerating,
    activeGenerations,
    loadingTextIndex,
    setLoadingTextIndex,
    showEncouragement,
    isInitialLoading,
    setLoadingTextAutoplay,

    // Prompt
    contentInput,
    setContentInput,
    contentEditableRef,
    handleInput,

    // Settings
    ...settings,

    // Cost
    services,
    estimatedCost,

    // Submission
    isSubmitting,
    handleSubmit,

    // Free Preview
    isFreePreviewEligible,

    // UI Refs
    controlsRef,
    containerRef,
  };
}
